import React from 'react';
import { X, User, Calendar, Star } from 'lucide-react';
import type { Feedback } from '../../../shared/lib/api';
import { formatBrasilia } from '../../../shared/lib/date';

interface Props {
  feedback: Feedback;
  onClose: () => void;
}

const DIMENSIONS = [
  { ratingKey: 'behaviorRating', commentKey: 'behaviorComment', label: 'Comportamento e Atitude' },
  { ratingKey: 'deliveriesRating', commentKey: 'deliveriesComment', label: 'Entregas e Resultados' },
  { ratingKey: 'qualityRating', commentKey: 'qualityComment', label: 'Qualidade das Atividades' },
  { ratingKey: 'collaborationRating', commentKey: 'collaborationComment', label: 'Colaboração e Trabalho em Equipe' },
  { ratingKey: 'punctualityRating', commentKey: 'punctualityComment', label: 'Pontualidade e Compromisso com Prazos' },
] as const;

const RatingBadge: React.FC<{ value: number }> = ({ value }) => {
  const colors = ['', 'bg-red-100 text-red-700', 'bg-orange-100 text-orange-700', 'bg-yellow-100 text-yellow-700', 'bg-blue-100 text-blue-700', 'bg-green-100 text-green-700'];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-sm font-semibold ${colors[value]}`}>
      <Star className="w-3.5 h-3.5" />
      {value}
    </span>
  );
};

export const FeedbackDetailModal: React.FC<Props> = ({ feedback, onClose }) => {
  const collaboratorName = feedback.collaborator?.name ?? feedback.collaboratorId;
  const managerName = feedback.manager?.name ?? feedback.managerId;
  const createdAt = formatBrasilia(feedback.createdAt);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Feedback</h2>
            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {collaboratorName}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {feedback.referencePeriod}
              </span>
              <span>{createdAt}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Meta */}
          <div className="flex gap-6 text-sm text-gray-600 bg-gray-50 rounded-lg p-4">
            <div><span className="font-medium text-gray-800">Gestor:</span> {managerName}</div>
            <div><span className="font-medium text-gray-800">Colaborador:</span> {collaboratorName}</div>
          </div>

          {/* Dimensões */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Avaliação por Dimensão</h3>
            {DIMENSIONS.map(d => (
              <div key={d.ratingKey} className="p-4 border border-gray-100 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-800">{d.label}</span>
                  <RatingBadge value={feedback[d.ratingKey] as number} />
                </div>
                {feedback[d.commentKey] && (
                  <p className="text-sm text-gray-600 mt-1">{feedback[d.commentKey]}</p>
                )}
              </div>
            ))}
          </div>

          {/* Feedback geral */}
          {(feedback.strengths || feedback.improvements || feedback.recommendations) && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Feedback Geral</h3>
              {feedback.strengths && (
                <div className="p-4 bg-green-50 border border-green-100 rounded-lg">
                  <p className="text-xs font-semibold text-green-700 uppercase mb-1">Pontos Fortes</p>
                  <p className="text-sm text-gray-800">{feedback.strengths}</p>
                </div>
              )}
              {feedback.improvements && (
                <div className="p-4 bg-yellow-50 border border-yellow-100 rounded-lg">
                  <p className="text-xs font-semibold text-yellow-700 uppercase mb-1">Pontos de Melhoria</p>
                  <p className="text-sm text-gray-800">{feedback.improvements}</p>
                </div>
              )}
              {feedback.recommendations && (
                <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
                  <p className="text-xs font-semibold text-blue-700 uppercase mb-1">Plano de Ação / Recomendações</p>
                  <p className="text-sm text-gray-800">{feedback.recommendations}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
